import { RedoButton } from "@redotech/arbiter/buttons/redo-button";
import { Flex } from "@redotech/arbiter/flex";
import { RedoModal } from "@redotech/arbiter/modal/redo-modal";
import { InteractiveMapLocation } from "@redotech/redo-common-model-location-common/interactive-map-location";
import { useTranslation } from "@redotech/redo-i18n";
import { LocationsMapModalBody } from "@redotech/redo-web/interactive-map/interactive-map";
import { useQuery } from "@tanstack/react-query";
import { memo, useCallback, useState } from "react";
import { LocationOutsideRadiusWarning } from "../../submission/screens/shipment-method/location-outside-radius-warning";

export interface InStoreLocationsSearchParams {
  draftReturnId?: string;
  returnId?: string;
}

export const InStoreLocationsButton = memo(function InStoreLocationsButton({
  draftReturnId,
  returnId,
  customerCountry,
  searchInStoreLocations,
}: {
  draftReturnId?: string;
  returnId?: string;
  customerCountry?: string;
  searchInStoreLocations: (
    params: InStoreLocationsSearchParams,
  ) => Promise<InteractiveMapLocation[]>;
}) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const renderLocationExtra = useCallback(
    (location: InteractiveMapLocation) => (
      <LocationOutsideRadiusWarning location={location} />
    ),
    [],
  );
  const query = useQuery({
    queryKey: ["inStoreLocations", draftReturnId, returnId],
    queryFn: () => searchInStoreLocations({ draftReturnId, returnId }),
    enabled: isOpen,
    staleTime: Infinity,
  });

  return (
    <Flex>
      <RedoButton
        hierarchy="secondary"
        onClick={() => setIsOpen(true)}
        text={t("See available locations")}
      />
      <RedoModal
        isOpen={isOpen}
        onModalCloseRequested={() => setIsOpen(false)}
        title={t("Find a store that accepts drop offs")}
      >
        <LocationsMapModalBody
          customerCountry={customerCountry}
          error={!!query.error}
          locations={query.data ?? null}
          pending={query.isPending && query.fetchStatus !== "idle"}
          renderLocationExtra={renderLocationExtra}
        />
      </RedoModal>
    </Flex>
  );
});

// eslint-disable-next-line @redotech/no-re-exports -- TODO: remove re-export, update all import sites
export { InteractiveMap } from "@redotech/redo-web/interactive-map/interactive-map";
